import { ReactNode, useCallback, useMemo, useRef } from 'react';
import { createFilterOptions, IconButton, Stack, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { ISimpleChoice } from '../../../data-models/field2.data-model';
import { IFormField, IFormFieldSelectMeta } from '../../../view-models/form.view-model';
import { formSelectInlineClassNames } from '../../../theme/component-styles';
import { Autocomplete, AutocompleteAction } from '../../Autocomplete/Autocomplete';
import { IBaseFieldProps } from './types';

export function FieldSelect(props: IBaseFieldProps<IFormFieldSelectMeta<SelectType | string>>) {
  const { formField, formProps } = props;
  const { ref, onChange, onBlur, value } = formProps;
  const inputRef = useRef<HTMLInputElement>(null);
  const { disabled = false, variant, disableClearable, selectOnFocus } = formField ?? {};
  const { inputEndAdornment } = formField.rendererMeta ?? {};
  const className = variant === 'form-inline' ? formSelectInlineClassNames : '';
  const {
    getOptionsLabel,
    options,
    getOptionDisabled,
    groupBy,
    renderOption,
    filterOptions,
    actionableProps,
  } = useFieldSelect(formField);

  const onAction = actionableProps?.onAction;
  const action = useMemo<AutocompleteAction | undefined>(
    () =>
      onAction
        ? {
            label: actionableProps?.actionLabel ?? '',
            helperText: actionableProps?.helperText,
            onAction: (inputValue?: string) => {
              inputRef.current?.blur();
              onAction(inputValue);
            },
          }
        : undefined,
    [actionableProps?.actionLabel, actionableProps?.helperText, onAction]
  );

  return (
    <Autocomplete<SelectType>
      ref={ref}
      inputRef={inputRef}
      className={className}
      action={action}
      disableClearable={disableClearable}
      disabled={disabled}
      filterOptions={filterOptions}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={getOptionsLabel}
      groupBy={groupBy}
      loading={formField.rendererMeta?.loading}
      isOptionEqualToValue={(option, current) => option === current}
      onBlur={onBlur}
      onChange={onChange}
      openOnFocus
      options={options}
      selectOnFocus={selectOnFocus}
      placeholder={formField.placeholder}
      autoFocus={formField.autoFocus}
      // The shared `Label` beside this renders no `htmlFor`; the input carries the name.
      aria-label={formField?.label}
      // Inside the input rather than beside it: an adornment outside takes width from the
      // field, so a column of them leaves every input a different size.
      inputEndAdornment={inputEndAdornment}
      renderOption={renderOption}
      value={value ?? null}
    />
  );
}

export type SelectType = string | number;

export function useFieldSelect(field: IFormField<IFormFieldSelectMeta<SelectType>>) {
  const actionableProps = field.rendererMeta?.actionMeta;
  const { onDelete } = actionableProps ?? {};

  const valuesAsMap = useMemo(() => {
    const values = field.rendererMeta?.values ?? [];
    return values.reduce((res, choice) => {
      return res.set(choice.value, choice);
    }, new Map<SelectType, ISimpleChoice<SelectType>>());
  }, [field.rendererMeta?.values]);

  const getOptionsLabel = useCallback(
    (option: SelectType | string) => {
      const choiceItem = valuesAsMap.get(option) ?? { displayName: '', value: '' };
      return choiceItem.displayName ?? String(choiceItem.value);
    },
    [valuesAsMap]
  );

  const getAllLabels = useCallback(
    (option: SelectType) => {
      const choiceItem = valuesAsMap.get(option) ?? { displayName: '', value: '' };
      return {
        label: choiceItem.displayName ?? String(choiceItem.value),
        secondaryLabel: choiceItem.secondaryLabel,
        caption: choiceItem.caption,
      };
    },
    [valuesAsMap]
  );

  const twoLine = field.rendererMeta?.optionLayout === 'two-line';
  const optionLimit = field.rendererMeta?.optionLimit;

  // Two-line options exist because the label alone does not distinguish them, so matching
  // only the label would leave the extra lines unsearchable. Scoped to two-line mode to
  // leave every other select's filtering exactly as it was.
  const _filterOptions = useMemo(
    () =>
      createFilterOptions<SelectType>({
        ...(twoLine
          ? {
              stringify: (option: SelectType) => {
                const { label, secondaryLabel, caption } = getAllLabels(option);
                return [label, secondaryLabel, caption, String(option)].filter(Boolean).join(' ');
              },
            }
          : {}),
        limit: optionLimit,
      }),
    [getAllLabels, optionLimit, twoLine]
  );
  const filterOptions = useCallback(
    (options: SelectType[], inputValue: string) =>
      _filterOptions(options, { inputValue, getOptionLabel: getOptionsLabel }),
    [_filterOptions, getOptionsLabel]
  );

  // The row's content; the Autocomplete wrapper owns the row itself.
  const renderOption = useCallback(
    (option: SelectType): ReactNode => {
      const { label, secondaryLabel, caption } = getAllLabels(option);
      // `noWrap` only in the two-line layout, where a wrapping sample would push the
      // caption onto a third line. Single-line options keep wrapping, as they always did.
      const adornments = (noWrapSecondary: boolean) => (
        <Stack direction={'row'} alignItems={'center'} gap='0.25rem'>
          {secondaryLabel && (
            <Typography variant='caption' color='text.secondary' noWrap={noWrapSecondary}>
              {' '}
              {secondaryLabel}
            </Typography>
          )}
          {onDelete && (
            <IconButton
              aria-label='delete'
              color={'secondary'}
              onClick={(event) => {
                event.stopPropagation();
                onDelete(option);
              }}
            >
              <DeleteOutlineIcon fontSize={'small'} />
            </IconButton>
          )}
        </Stack>
      );

      if (twoLine) {
        return (
          <Stack direction={'column'} width={'100%'}>
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              gap='1rem'
              width={'100%'}
            >
              <Typography variant='body2' noWrap title={label}>
                {label}
              </Typography>
              {adornments(true)}
            </Stack>
            {caption && (
              <Typography variant='caption' color='text.secondary' noWrap width={'100%'} title={caption}>
                {caption}
              </Typography>
            )}
          </Stack>
        );
      }

      return (
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} width={'100%'}>
          <Typography variant='body2' noWrap title={label}>
            {label}
          </Typography>
          {adornments(false)}
        </Stack>
      );
    },
    [getAllLabels, onDelete, twoLine]
  );

  const groupBy = useMemo(() => {
    if (!field.rendererMeta?.enableGrouping) {
      return undefined;
    }
    return (option: SelectType) => {
      return valuesAsMap.get(option)?.group ?? '';
    };
  }, [field.rendererMeta?.enableGrouping, valuesAsMap]);

  return {
    filterOptions: twoLine || optionLimit != null ? filterOptions : undefined,
    getOptionsLabel,
    groupBy,
    options: Array.from(valuesAsMap.keys()),
    valuesAsMap,
    getOptionDisabled: (option: SelectType) => valuesAsMap.get(option)?.disabled ?? false,
    actionableProps,
    renderOption,
  };
}
